import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getCurrentViewportTier, isMobileOrTabletTier } from "./viewport.js";

gsap.registerPlugin(ScrollTrigger);

const STRIPE_PALETTE = ["#0D1E79"];

export async function initDonutComparison() {
  const section = document.querySelector(".donut-comparison");
  if (!section) return;

  const compactLayout = isMobileOrTabletTier(getCurrentViewportTier());

  const canvas = section.querySelector(".donut-comparison__canvas");
  if (!canvas) return;

  const chartsData = JSON.parse(section.dataset.charts || "[]");
  if (chartsData.length < 2) return;

  const { Chart } = await import(
    /* webpackChunkName: "chart-loader" */ "./chart-loader.js"
  );

  // =====================================================================
  // Plugin
  // =====================================================================
  const stripedSegmentPlugin = {
    id: "stripedSegment",
    beforeDatasetDraw(chart) {
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((arc, i) => {
        if (chart.data.datasets[0].striped?.[i]) {
          arc.hidden = true;
        }
      });
    },
    afterDatasetDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const dataset = chart.data.datasets[0];
      const stripes = 25;

      meta.data.forEach((arc, i) => {
        if (!dataset.striped?.[i]) return;

        arc.hidden = false;

        const { x, y, innerRadius, outerRadius, startAngle, endAngle } = arc;
        const angleStep = (endAngle - startAngle) / stripes;
        const gap = angleStep * 0.1;

        if (!dataset._stripeColors?.[i]) {
          if (!dataset._stripeColors) dataset._stripeColors = {};
          dataset._stripeColors[i] = Array.from(
            { length: stripes },
            () =>
              STRIPE_PALETTE[Math.floor(Math.random() * STRIPE_PALETTE.length)],
          );
        }

        const colors = dataset._stripeColors[i];

        const cornerRadius = 600;

        ctx.save();
        for (let s = 0; s < stripes; s++) {
          const sAngle = startAngle + s * angleStep;
          const eAngle = sAngle + angleStep - gap;

          // Clamp the rounding so it never exceeds half the stripe in any
          // direction (radial thickness, or arc length on either edge).
          const radialHalf = (outerRadius - innerRadius) / 2;
          const arcHalfInner = (innerRadius * (eAngle - sAngle)) / 2;
          const r = Math.max(
            0,
            Math.min(cornerRadius, radialHalf, arcHalfInner),
          );

          // Convert the corner radius to angular insets at each radius
          // (arc-length r at radius R subtends an angle of r / R).
          const dOuter = r / outerRadius;
          const dInner = r / innerRadius;

          // Polar -> cartesian helper bound to this segment's center.
          const px = (radius, angle) => x + radius * Math.cos(angle);
          const py = (radius, angle) => y + radius * Math.sin(angle);

          ctx.beginPath();

          // Start on the outer arc, inset by dOuter from sAngle.
          ctx.moveTo(
            px(outerRadius, sAngle + dOuter),
            py(outerRadius, sAngle + dOuter),
          );

          // Outer arc (curved edge, shortened on both ends by dOuter).
          ctx.arc(x, y, outerRadius, sAngle + dOuter, eAngle - dOuter);

          // Rounded corner: outer arc -> radial edge at eAngle.
          // Control point is the true (un-rounded) corner; this bends the
          // path inward exactly like CSS border-radius on a rectangle.
          ctx.quadraticCurveTo(
            px(outerRadius, eAngle),
            py(outerRadius, eAngle),
            px(outerRadius - r, eAngle),
            py(outerRadius - r, eAngle),
          );

          // Straight radial edge at eAngle, shortened by r on both ends.
          ctx.lineTo(px(innerRadius + r, eAngle), py(innerRadius + r, eAngle));

          // Rounded corner: radial edge -> inner arc at eAngle.
          ctx.quadraticCurveTo(
            px(innerRadius, eAngle),
            py(innerRadius, eAngle),
            px(innerRadius, eAngle - dInner),
            py(innerRadius, eAngle - dInner),
          );

          // Inner arc (reverse direction), shortened on both ends by dInner.
          ctx.arc(x, y, innerRadius, eAngle - dInner, sAngle + dInner, true);

          // Rounded corner: inner arc -> radial edge at sAngle.
          ctx.quadraticCurveTo(
            px(innerRadius, sAngle),
            py(innerRadius, sAngle),
            px(innerRadius + r, sAngle),
            py(innerRadius + r, sAngle),
          );

          // Straight radial edge at sAngle.
          ctx.lineTo(px(outerRadius - r, sAngle), py(outerRadius - r, sAngle));

          // Rounded corner: radial edge -> outer arc at sAngle, closing the loop.
          ctx.quadraticCurveTo(
            px(outerRadius, sAngle),
            py(outerRadius, sAngle),
            px(outerRadius, sAngle + dOuter),
            py(outerRadius, sAngle + dOuter),
          );

          ctx.closePath();
          ctx.fillStyle = colors[s];
          ctx.fill();
        }
        ctx.restore();
      });
    },
  };
  // =====================================================================
  // Plugin
  // =====================================================================
  const legendPaddingPlugin = {
    id: "legendPadding",
    beforeInit(chart) {
      const originalFit = chart.legend.fit;
      chart.legend.fit = function () {
        originalFit.bind(chart.legend)();
        this.height += 24;
      };
    },
  };

  // =====================================================================
  // Plugin — one-shot first-render signal
  // ---------------------------------------------------------------------
  // Resolves `firstRender` after Chart.js completes its initial render so
  // the caller can safely call ScrollTrigger.refresh() against a chart
  // canvas that has its final pixel size.
  // =====================================================================
  let resolveFirstRender;
  const firstRender = new Promise((resolve) => {
    resolveFirstRender = resolve;
  });
  const firstRenderPlugin = {
    id: "firstRender",
    afterRender() {
      if (resolveFirstRender) {
        resolveFirstRender();
        resolveFirstRender = null;
      }
    },
  };

  // =====================================================================
  // Dataset builder
  // =====================================================================
  const toDataset = (segments) => ({
    data: segments.map((s) => s.value),
    backgroundColor: segments.map((s) => s.color),
    striped: segments.map((s) => s.striped ?? false),
    borderWidth: 4,
    borderColor: "#ffffff",
    borderRadius: 12,
    borderSkipped: false,
    hoverOffset: 8,
  });

  // =====================================================================
  // Chart
  // =====================================================================
  const chart = new Chart(canvas, {
    type: "doughnut",
    plugins: [stripedSegmentPlugin, legendPaddingPlugin, firstRenderPlugin],
    data: {
      labels: chartsData[0].segments.map((s) => s.label),
      datasets: [toDataset(chartsData[0].segments)],
    },
    options: {
      layout: {
        padding: compactLayout ? 20 : 80,
      },
      cutout: compactLayout ? 55 : 65,
      responsive: true,
      maintainAspectRatio: true,
      animation: { duration: 800, easing: "easeInOutQuart" },
      plugins: {
        tooltip: { enabled: false },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            padding: 20,
            boxWidth: 12,
            boxHeight: 12,
            usePointStyle: true,
            pointStyle: "circle",
            font: { size: 13, family: "Lexend", weight: 300 },
            generateLabels: (chart) => {
              const data = chart.data;
              return data.labels.map((label, i) => ({
                text: `${label} ${data.datasets[0].data[i]}%`,
                fillStyle: data.datasets[0].backgroundColor[i],
                strokeStyle: data.datasets[0].backgroundColor[i],
                index: i,
              }));
            },
          },
        },
      },
    },
  });

  // =====================================================================
  // Update helper
  // =====================================================================
  const updateChart = (index) => {
    const data = chartsData[index];
    if (!data) return;
    chart.data.labels = data.segments.map((s) => s.label);
    chart.data.datasets[0].data = data.segments.map((s) => s.value);
    chart.data.datasets[0].backgroundColor = data.segments.map((s) => s.color);
    chart.data.datasets[0].striped = data.segments.map(
      (s) => s.striped ?? false,
    );
    chart.update();
  };

  // =====================================================================
  // ScrollTrigger
  // =====================================================================
  const SCROLL_PER_CHART = window.innerHeight * 0.8;
  const totalScroll = SCROLL_PER_CHART * (chartsData.length - 1);
  let currentIndex = 0;

  ScrollTrigger.create({
    trigger: section,
    start: compactLayout ? "30% top" : "top 10%",
    end: () => `+=${totalScroll}`,
    pin: true,
    pinSpacing: true,
    // markers: true,
    invalidateOnRefresh: true,
    onUpdate: (self) => {
      const index = Math.round(self.progress * (chartsData.length - 1));
      if (index !== currentIndex) {
        currentIndex = index;
        updateChart(index);
      }
    },
  });

  await firstRender;
}
